import { RefObject, useEffect, useRef } from "react";

/** Horizontal finger travel that counts as a swipe. */
const MIN_DISTANCE_PX = 60;
/** Slower drags are reading/scrolling, not a swipe. */
const MAX_DURATION_MS = 800;
/** The swipe has to be clearly horizontal: |dx| >= ratio * |dy|. */
const MIN_DIRECTION_RATIO = 1.5;

const IGNORED_TARGETS =
  'input, textarea, select, [contenteditable="true"], [role="slider"], [data-no-swipe]';

/** True when the touch starts on a control or inside a horizontally scrollable box. */
const ignoresSwipe = (target: EventTarget | null, root: HTMLElement) => {
  if (!(target instanceof Element)) return false;
  if (target.closest(IGNORED_TARGETS)) return true;
  for (
    let el: Element | null = target;
    el && el !== root;
    el = el.parentElement
  ) {
    if (el.scrollWidth > el.clientWidth) {
      const { overflowX } = getComputedStyle(el);
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
  }
  return false;
};

/** A one-finger drag moves a pinch-zoomed page instead of swiping. */
const isPageZoomed = () => (window.visualViewport?.scale ?? 1) > 1.01;

interface UseSwipeOptions {
  /** finger moved right to left */
  onSwipeLeft?: () => void;
  /** finger moved left to right */
  onSwipeRight?: () => void;
  enabled?: boolean;
}

/**
 * Horizontal one-finger swipe on a touch screen. Native listeners on the element, so touches
 * in portals (dialogs, zoomed images) rendered elsewhere in the DOM do not count.
 */
export function useSwipe(
  ref: RefObject<HTMLElement>,
  { onSwipeLeft, onSwipeRight, enabled = true }: UseSwipeOptions,
) {
  const handlers = useRef({ onSwipeLeft, onSwipeRight });
  useEffect(() => {
    handlers.current = { onSwipeLeft, onSwipeRight };
  });

  useEffect(() => {
    const root = ref.current;
    if (!root || !enabled) return;

    let start: { x: number; y: number; time: number } | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (
        e.touches.length !== 1 ||
        isPageZoomed() ||
        ignoresSwipe(e.target, root)
      ) {
        start = null;
        return;
      }
      const touch = e.touches[0];
      start = { x: touch.clientX, y: touch.clientY, time: e.timeStamp };
    };

    const onTouchMove = (e: TouchEvent) => {
      // a second finger means pinch zoom
      if (e.touches.length > 1) start = null;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!start) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const elapsed = e.timeStamp - start.time;
      start = null;
      if (
        elapsed > MAX_DURATION_MS ||
        Math.abs(dx) < MIN_DISTANCE_PX ||
        Math.abs(dx) < MIN_DIRECTION_RATIO * Math.abs(dy) ||
        isPageZoomed() ||
        !!window.getSelection()?.toString()
      ) {
        return;
      }
      if (dx < 0) handlers.current.onSwipeLeft?.();
      else handlers.current.onSwipeRight?.();
    };

    const onTouchCancel = () => {
      start = null;
    };

    // Chrome turns a horizontal overscroll on a touch screen into history
    // back/forward on top of the swipe; switched off while swiping is on
    // (not for a mouse/touchpad, where it is the user's back gesture)
    const html = document.documentElement;
    const previousOverscroll = html.style.overscrollBehaviorX;
    const touchScreen = window.matchMedia("(pointer: coarse)").matches;
    if (touchScreen) html.style.overscrollBehaviorX = "none";

    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: true });
    root.addEventListener("touchend", onTouchEnd);
    root.addEventListener("touchcancel", onTouchCancel);
    return () => {
      if (touchScreen) html.style.overscrollBehaviorX = previousOverscroll;
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      root.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [ref, enabled]);
}
